import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Calendar, Save } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const STATUS_LABELS = {
  trial: "Free trial",
  active: "Active",
  overdue: "Overdue",
  inactive: "Inactive",
};

const STATUS_COLORS = {
  trial: { bg: "#DBEAFE", fg: "#1E40AF" },
  active: { bg: "#D1FAE5", fg: "#065F46" },
  overdue: { bg: "#FEE2E2", fg: "#991B1B" },
  inactive: { bg: "#F3F4F6", fg: "#374151" },
};

const PLAN_LABELS = {
  mentor_basic: "Basic",
  mentor_professional: "Professional",
};

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export function AdminMentorSubscriptionsPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [launchDate, setLaunchDate] = useState("");
  const [launchInput, setLaunchInput] = useState("");
  const [savingLaunch, setSavingLaunch] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async (status) => {
    setLoading(true);
    try {
      const [rowsRes, launchRes] = await Promise.all([
        api.get("/admin/mentor-subscriptions", { params: status && status !== "all" ? { status } : {} }),
        api.get("/admin/mentor-subscriptions/launch-date"),
      ]);
      setRows(rowsRes.data.rows || []);
      setTotal(rowsRes.data.total || 0);
      const dt = launchRes.data.launch_date;
      setLaunchDate(dt || "");
      setLaunchInput(dt ? dt.slice(0, 10) : "");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(filter); }, [filter]);

  const saveLaunchDate = async () => {
    if (!launchInput) {
      toast.error("Pick a date first.");
      return;
    }
    setSavingLaunch(true);
    try {
      await api.put("/admin/mentor-subscriptions/launch-date", { launch_date: launchInput });
      toast.success("Launch date saved.");
      await load(filter);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSavingLaunch(false); }
  };

  const toggleVerified = async (row) => {
    setBusyId(row.user_id);
    try {
      await api.post(`/admin/mentors/${row.user_id}/verify`, { verified: !row.verified });
      toast.success(row.verified ? "Verified badge removed." : "Verified badge granted.");
      await load(filter);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-mentor-subscriptions-panel">
      {/* Launch date config */}
      <div className="bg-white rounded-2xl p-5 border border-black/5">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="h-4 w-4" style={{ color: "#13A699" }} />
          <h3 className="font-display font-bold uppercase" style={{ color: "#13A699" }}>Launch Date</h3>
        </div>
        <p className="text-xs mb-3" style={{ color: "#13A699" }}>
          Set the date when the Mentor Basic 3-month free trial clock starts. Mentors who registered on or after this date get 3 months free from their registration date. If unset, enforcement is disabled (no mentor gets locked out).
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="date"
            value={launchInput}
            onChange={(e) => setLaunchInput(e.target.value)}
            className="rounded-xl border border-black/10 px-3 py-2 text-sm"
            data-testid="admin-launch-date-input"
          />
          <button
            onClick={saveLaunchDate}
            disabled={savingLaunch}
            className="btn-yellow text-sm px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="admin-launch-date-save"
          >
            <Save className="h-4 w-4" /> {savingLaunch ? "Saving…" : "Save launch date"}
          </button>
          {launchDate && (
            <span className="text-xs" style={{ color: "#13A699" }} data-testid="admin-launch-date-current">
              Current: <b>{formatDate(launchDate)}</b>
            </span>
          )}
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="admin-mentor-sub-filters">
        {["all", "trial", "active", "overdue", "inactive"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`text-xs font-bold uppercase px-3 py-1.5 rounded-full ${filter === s ? "bg-[#13A699] text-white" : "bg-white border border-black/10 text-[#13A699]"}`}
            data-testid={`admin-mentor-sub-filter-${s}`}
          >
            {s === "all" ? "All" : STATUS_LABELS[s]}
          </button>
        ))}
        <span className="text-xs ml-2" style={{ color: "#13A699" }}>Total: <b>{total}</b></span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead style={{ background: "#FFF7ED" }}>
            <tr style={{ color: "#13A699" }}>
              <th className="text-left px-4 py-3 font-bold">Mentor</th>
              <th className="text-left px-4 py-3 font-bold">Plan</th>
              <th className="text-left px-4 py-3 font-bold">Status</th>
              <th className="text-left px-4 py-3 font-bold">Trial ends</th>
              <th className="text-left px-4 py-3 font-bold">Sub expires</th>
              <th className="text-left px-4 py-3 font-bold">Verified</th>
              <th className="text-left px-4 py-3 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-black/60">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-black/50" data-testid="admin-mentor-sub-empty">No mentors match this filter.</td></tr>
            )}
            {!loading && rows.map((r) => {
              const color = STATUS_COLORS[r.status] || STATUS_COLORS.inactive;
              const isPro = r.plan === "mentor_professional" && r.status === "active";
              return (
                <tr key={r.user_id} className="border-t border-black/5" data-testid={`admin-mentor-sub-row-${r.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold" style={{ color: "#13A699" }}>{r.name || "—"}</div>
                    <div className="text-xs text-black/50">{r.email}</div>
                  </td>
                  <td className="px-4 py-3">{PLAN_LABELS[r.plan] || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-bold uppercase px-2 py-1 rounded-full" style={{ background: color.bg, color: color.fg }}>
                      {STATUS_LABELS[r.status] || r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">{formatDate(r.trial_ends_at)}</td>
                  <td className="px-4 py-3">{formatDate(r.expires_at)}</td>
                  <td className="px-4 py-3">
                    {r.verified ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-blue-800" data-testid={`admin-mentor-verified-${r.user_id}`}>
                        <ShieldCheck className="h-4 w-4" /> Yes
                      </span>
                    ) : (
                      <span className="text-xs text-black/40">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleVerified(r)}
                      disabled={busyId === r.user_id || (!r.verified && !isPro)}
                      title={!isPro && !r.verified ? "Requires active Professional subscription" : ""}
                      className={`text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1 disabled:opacity-40 ${r.verified ? "bg-red-100 text-red-700 border border-red-300" : "bg-blue-600 text-white"}`}
                      data-testid={`admin-mentor-verify-toggle-${r.user_id}`}
                    >
                      {r.verified ? (<><ShieldOff className="h-3.5 w-3.5" /> Remove badge</>) : (<><ShieldCheck className="h-3.5 w-3.5" /> Grant badge</>)}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
